<script setup lang="ts">
import { computed, reactive, ref, defineAsyncComponent, getCurrentInstance, onMounted } from 'vue'
import type { Pinia } from 'pinia'
import Scheme from './Scheme/index.vue'
import UnavailableScheme from './UnavailableScheme.vue'
import AtlasButton from '@/components/Common/AtlasButton.vue';
import { featureFlagsStore } from '@/stores/featureFlagsStore'
// Lazy-loaded so jspdf/handlebars stay out of vueApp's eager graph (and out of
// the dev watcher's incremental rebuild, which deadlocks with them in-graph on Windows).
const PdfGenerator = defineAsyncComponent(() => import('@/components/Form/pdfGenerator/index.vue'));
const props = defineProps<{
    schemes: any[]
    loading?: boolean
    productId: string
    quoteId: string
    action: string
    showPrintResults?: boolean
    showSchemeTitles?: boolean
}>()

defineOptions({
  name: 'QuoteSchemes',
})

const emit = defineEmits<{
    (e: 'select', id: string): void
}>()

const comparedSchemes = ref<any[]>([])
const showCompare = ref(false)

// Resolved once here rather than per card - a household quote can return 60+ schemes.
const ipidLinkEnabled = ref(false)

const { appContext } = getCurrentInstance()!
const ffStore = featureFlagsStore(appContext.app._context.provides.pinia as Pinia)

onMounted(async () => {
    try {
        ipidLinkEnabled.value = await ffStore.isFeatureEnabled('temp-docs-v3-addon-documents')
    } catch {
        // The store throws when FeatureHub hasn't come up; leaving the link hidden is the safe default.
        ipidLinkEnabled.value = false
    }
})

const availableSchemes = computed(() =>
    (props.schemes ?? []).filter((s: any) => !s?.IsReferred && !s?.IsDeclined)
)

const unavailableSchemes = computed(() =>
    (props.schemes ?? []).filter((s: any) => s?.IsReferred || s?.IsDeclined)
)

function onSelect(id: string) {
    emit('select', id)
}

function onCompare(id: string) {
    const scheme = availableSchemes.value.find((s: any) => s.SchemeId === id)
    if (scheme && !comparedSchemes.value.includes(scheme)) {
        comparedSchemes.value.push(scheme)
    } else {
        const index = comparedSchemes.value.findIndex((s: any) => s.SchemeId === id)
        if (index !== -1) {
            comparedSchemes.value.splice(index, 1)
        }
    }
}
</script>

<template>
    <section class="mt-4 position-relative">
        <div v-if="loading" class="position-absolute w-100 h-100 d-flex align-items-start justify-content-center pt-5" style="inset: 0; background: rgba(255, 255, 255, 0.6); z-index: 10;">
            <span class="spinner-border text-info" role="status" aria-hidden="true"></span>
        </div>
        <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center flex-grow-1 mr-3">
                <h2 class="h2 font-weight-bold text-dark mb-0 mr-3">
                    Best Matches
                </h2>
                <div class="border-top flex-grow-1"></div>
            </div>

            <div class="small text-uppercase text-muted font-weight-bold text-nowrap">
                {{ availableSchemes.length }} Provider{{ availableSchemes.length === 1 ? '' : 's' }} Available
            </div>
        </div>

        <div class="d-flex mb-3 justify-content-end">
            <PdfGenerator buttonText="Print Results" :schemes="availableSchemes" class="mr-2" v-if="props.showPrintResults"></PdfGenerator>
            <AtlasButton
            @click="showCompare = false"
            class="mr-2"
            >
                <span>All Results</span>
            </AtlasButton>
            <AtlasButton
            variant="primary"
            :outline="true"
            @click="showCompare = true"
            >
                <span>Compare ({{ comparedSchemes.length }})</span>
            </AtlasButton>
        </div>

        <div v-if="!showCompare">
            <div v-if="availableSchemes.length === 0" class="text-center text-muted my-5">
                No providers available.
            </div>
            <div v-else>
                <Scheme
                    v-for="scheme in availableSchemes"
                    :key="scheme.SchemeId"
                    :scheme="scheme"
                    @select="onSelect"
                    @compare="onCompare"
                    :compared="comparedSchemes.some((s) => s.SchemeId === scheme.SchemeId)"
                    :product-id="productId"
                    :quote-id="quoteId"
                    :action="action"
                    :ipid-link-enabled="ipidLinkEnabled"
                    :show-scheme-titles="props.showSchemeTitles"
                />
            </div>
        </div>
        <div v-else>
            <div v-if="comparedSchemes.length === 0" class="text-center text-muted my-5">
                No providers selected for comparison.
            </div>
            <div v-else>
                <Scheme
                    v-for="scheme in comparedSchemes"
                    :key="scheme.SchemeId"
                    :scheme="scheme"
                    @select="onSelect"
                    @compare="onCompare"
                    :compared="true"
                    :product-id="productId"
                    :quote-id="quoteId"
                    :action="action"
                    :ipid-link-enabled="ipidLinkEnabled"
                    :show-scheme-titles="props.showSchemeTitles"
                />
            </div>
        </div>

        <div class="d-flex justify-content-between align-items-center pt-3" v-if="unavailableSchemes.length">
            <div class="d-flex align-items-center flex-grow-1 mr-3">
                <h2 class="h2 font-weight-bold text-dark mb-0 mr-3">
                    Unavailable Providers
                </h2>
                <div class="border-top flex-grow-1"></div>
            </div>

            <div class="small text-uppercase text-muted font-weight-bold text-nowrap">
                {{ unavailableSchemes.length }} Provider{{ unavailableSchemes.length === 1 ? '' : 's' }} Unavailable
            </div>
        </div>
        <div v-if="unavailableSchemes.length" class="row mt-3">
            <UnavailableScheme
                v-for="scheme in unavailableSchemes"
                :key="scheme.SchemeId"
                :scheme="scheme"
            />
        </div>
    </section>
</template>